import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from './AppIcon';
import { useLanguage } from '../context/LanguageContext';

const BANNER_DISMISSED_KEY = 'lumiveta_install_banner_dismissed';
const DISMISS_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

const InstallBanner = () => {
  const { language } = useLanguage();
  const isSpanish = language === 'es';
  
  const [showBanner, setShowBanner] = useState(false);
  const [showIOSTooltip, setShowIOSTooltip] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed as PWA
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if banner was dismissed within last 24 hours
    const dismissedTime = localStorage.getItem(BANNER_DISMISSED_KEY);
    if (dismissedTime) {
      const timeSinceDismissed = Date.now() - parseInt(dismissedTime, 10);
      if (timeSinceDismissed < DISMISS_DURATION) {
        return; // Don't show banner
      }
    }

    // Show on mobile browsers (not installed as PWA)
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isSmallScreen = window.innerWidth <= 768;
    
    if (!standalone && (isMobile || (hasTouchScreen && isSmallScreen) || isSmallScreen)) {
      setShowBanner(true);
    }

    // Listen for Android beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIOS) {
      // Show iOS instructions tooltip
      setShowIOSTooltip(true);
      setTimeout(() => setShowIOSTooltip(false), 5000);
    } else if (deferredPrompt) {
      // Trigger Android native install prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowBanner(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(BANNER_DISMISSED_KEY, Date.now().toString());
    setShowBanner(false);
  };

  // Don't render if already installed or banner shouldn't show
  if (isStandalone || !showBanner) return null;

  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          className="fixed left-0 right-0 z-[100] bg-gradient-to-r from-[#0A0E1A] to-[#1a1f2e] border-b border-primary/30 shadow-lg"
          style={{ 
            top: 'calc(64px + env(safe-area-inset-top))',
          }}
        >
          <div className="flex items-center justify-between px-3 py-2.5">
            <div className="flex items-center gap-2 flex-1">
              <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                <Icon name="Download" size={16} color="#FFD700" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm font-medium text-foreground truncate">
                  {isSpanish ? 'Instalar Lumiveta' : 'Install Lumiveta'}
                </p>
                <p className="text-[10px] sm:text-xs text-muted-foreground truncate">
                  {isSpanish ? 'Experiencia de pantalla completa' : 'Full-screen experience'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={handleInstallClick}
                className="px-3 py-1.5 bg-primary text-primary-foreground text-xs font-semibold rounded-full hover:bg-primary/90 transition-colors"
                data-testid="install-banner-btn"
              >
                {isSpanish ? 'Instalar' : 'Install'}
              </button>
              <button
                onClick={handleDismiss}
                className="p-1.5 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={isSpanish ? 'Cerrar' : 'Dismiss'}
                data-testid="install-banner-dismiss"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* iOS Installation Tooltip */}
      <AnimatePresence>
        {showIOSTooltip && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="fixed left-4 right-4 z-[10000] glass rounded-xl p-4 border border-primary/30 shadow-xl"
            style={{ top: 'calc(140px + env(safe-area-inset-top))' }}
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                <Icon name="Share" size={20} color="#FFD700" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground text-sm mb-1">
                  {isSpanish ? 'Agregar a Pantalla de Inicio' : 'Add to Home Screen'}
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {isSpanish 
                    ? <>Toca el ícono <span className="inline-flex items-center mx-1"><Icon name="Share" size={12} color="#FFD700" /></span> Compartir en la parte inferior de Safari, luego toca <strong className="text-primary">"Agregar a Pantalla de Inicio"</strong></>
                    : <>Tap the <span className="inline-flex items-center mx-1"><Icon name="Share" size={12} color="#FFD700" /></span> Share icon at the bottom of Safari, then tap <strong className="text-primary">"Add to Home Screen"</strong></>
                  }
                </p>
              </div>
              <button
                onClick={() => setShowIOSTooltip(false)}
                className="p-1 text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={14} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default InstallBanner;
